import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, MessageSquare, TrendingUp, User } from 'lucide-react';
import { crmAPI } from '../../lib/api';
import toast from 'react-hot-toast';

const STAGES = ['new', 'contacted', 'interested', 'qualified', 'proposal_sent', 'booked', 'lost'];
const STAGE_COLORS = {
  new: { color: '#6B7280', bg: '#F3F4F6' },
  contacted: { color: '#2563EB', bg: '#EFF6FF' },
  interested: { color: '#7C3AED', bg: '#F5F3FF' },
  qualified: { color: '#D97706', bg: '#FFFBEB' },
  proposal_sent: { color: '#0891B2', bg: '#ECFEFF' },
  booked: { color: '#059669', bg: '#ECFDF5' },
  lost: { color: '#DC2626', bg: '#FEF2F2' },
};

const CRMPage = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [view, setView] = useState('list');
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });

  const fetchLeads = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (stageFilter) params.stage = stageFilter;
      const [leadsRes, pipelineRes] = await Promise.all([
        crmAPI.getAll(params),
        crmAPI.getPipeline()
      ]);
      setLeads(leadsRes.data.data || []);
      setPipeline(pipelineRes.data.data?.pipeline || []);
      setPagination(leadsRes.data.pagination || {});
    } catch {
      toast.error('Failed to load CRM data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLeads(); }, [search, stageFilter]);

  const handleStageChange = async (leadId, newStage) => {
    try {
      await crmAPI.updateStage(leadId, { stage: newStage });
      toast.success('Stage updated');
      fetchLeads();
    } catch {
      toast.error('Stage update failed');
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">CRM & Lead Management</h1>
          <p className="page-subtitle">{pagination.total} leads total</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {['list', 'kanban'].map(v => (
              <button key={v} className={`btn btn-sm ${view === v ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setView(v)} style={{ textTransform: 'capitalize' }}>{v}</button>
            ))}
          </div>
          <button className="btn btn-primary" onClick={() => navigate('/crm/new')}>
            <Plus size={16} /> Add Lead
          </button>
        </div>
      </div>

      {/* Pipeline Summary */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, overflowX: 'auto', paddingBottom: 4 }}>
        {pipeline.map(p => (
          <button
            key={p.stage}
            onClick={() => setStageFilter(stageFilter === p.stage ? '' : p.stage)}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              padding: '10px 16px', borderRadius: 10, border: `2px solid ${stageFilter === p.stage ? STAGE_COLORS[p.stage]?.color : 'transparent'}`,
              background: stageFilter === p.stage ? STAGE_COLORS[p.stage]?.bg : '#fff',
              cursor: 'pointer', minWidth: 90, boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
            }}
          >
            <span style={{ fontSize: 20, fontWeight: 700, color: STAGE_COLORS[p.stage]?.color }}>{p.count}</span>
            <span style={{ fontSize: 11, color: '#6B7280', textTransform: 'capitalize', textAlign: 'center', marginTop: 2 }}>
              {p.stage.replace('_', ' ')}
            </span>
          </button>
        ))}
      </div>

      <div className="filters-bar">
        <div className="search-bar">
          <Search size={16} />
          <input placeholder="Search name, email, phone..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-select" style={{ width: 180 }} value={stageFilter} onChange={e => setStageFilter(e.target.value)}>
          <option value="">All Stages</option>
          {STAGES.map(s => <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
        </select>
        {(search || stageFilter) && (
          <button className="btn btn-sm btn-secondary" onClick={() => { setSearch(''); setStageFilter(''); }}>Clear</button>
        )}
      </div>

      {loading ? (
        <div className="loading-center"><div className="spinner spinner-lg" /></div>
      ) : view === 'list' ? (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr><th>Lead</th><th>Contact</th><th>Source</th><th>Stage</th><th>Follow Up</th><th>Assigned</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {leads.length === 0 ? (
                  <tr><td colSpan={7}>
                    <div className="empty-state"><MessageSquare size={40} /><h3>No leads found</h3></div>
                  </td></tr>
                ) : leads.map(l => (
                  <tr key={l._id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/crm/${l._id}`)}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div className="avatar avatar-sm" style={{ background: '#F5F3FF', color: '#7C3AED' }}>
                          {l.firstName?.[0]}{l.lastName?.[0]}
                        </div>
                        <div>
                          <div style={{ fontWeight: 500 }}>{l.firstName} {l.lastName}</div>
                          <div style={{ fontSize: 12, color: '#9CA3AF' }}>{new Date(l.createdAt).toLocaleDateString()}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style={{ fontSize: 13 }}>{l.email}</div>
                      {l.phone && <div style={{ fontSize: 12, color: '#9CA3AF' }}>{l.phone}</div>}
                    </td>
                    <td><span className="badge badge-gray" style={{ textTransform: 'capitalize' }}>{l.source?.replace('_', ' ')}</span></td>
                    <td>
                      <select
                        className="form-select"
                        style={{ padding: '4px 8px', fontSize: 12, width: 140, color: STAGE_COLORS[l.stage]?.color, background: STAGE_COLORS[l.stage]?.bg, border: `1px solid ${STAGE_COLORS[l.stage]?.color}20` }}
                        value={l.stage}
                        onClick={e => e.stopPropagation()}
                        onChange={e => { e.stopPropagation(); handleStageChange(l._id, e.target.value); }}
                      >
                        {STAGES.map(s => <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
                      </select>
                    </td>
                    <td style={{ fontSize: 13, color: l.followUpDate && new Date(l.followUpDate) < new Date() ? '#DC2626' : '#374151' }}>
                      {l.followUpDate ? new Date(l.followUpDate).toLocaleDateString() : '—'}
                    </td>
                    <td>
                      {l.assignedTo ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div className="avatar avatar-sm">{l.assignedTo.firstName?.[0]}</div>
                          <span style={{ fontSize: 13 }}>{l.assignedTo.firstName}</span>
                        </div>
                      ) : <span style={{ color: '#9CA3AF', fontSize: 13 }}>Unassigned</span>}
                    </td>
                    <td>
                      <button className="btn btn-sm btn-secondary" onClick={e => { e.stopPropagation(); navigate(`/crm/${l._id}`); }}>View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* Kanban view */
        <div style={{ display: 'flex', gap: 14, overflowX: 'auto', paddingBottom: 16 }}>
          {STAGES.slice(0, 5).map(stage => {
            const stageLeads = leads.filter(l => l.stage === stage);
            return (
              <div key={stage} style={{ minWidth: 240, background: '#F9FAFB', borderRadius: 10, padding: 12, flexShrink: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, textTransform: 'capitalize', color: STAGE_COLORS[stage]?.color }}>
                    {stage.replace('_', ' ')}
                  </span>
                  <span className="badge" style={{ color: STAGE_COLORS[stage]?.color, background: STAGE_COLORS[stage]?.bg }}>{stageLeads.length}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {stageLeads.map(l => (
                    <div key={l._id} onClick={() => navigate(`/crm/${l._id}`)}
                      style={{ background: '#fff', borderRadius: 8, padding: 12, border: '1px solid #E5E7EB', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
                      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'}>
                      <div style={{ fontWeight: 500, fontSize: 14 }}>{l.firstName} {l.lastName}</div>
                      <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>{l.email}</div>
                      {l.travelDates?.checkIn && (
                        <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                          📅 {new Date(l.travelDates.checkIn).toLocaleDateString()}
                        </div>
                      )}
                      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 6, textTransform: 'capitalize' }}>{l.source?.replace('_', ' ')}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {pagination.pages > 1 && view === 'list' && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 24 }}>
          {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(p => (
            <button key={p} className={`btn btn-sm ${pagination.page === p ? 'btn-primary' : 'btn-secondary'}`} onClick={() => fetchLeads(p)}>{p}</button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CRMPage;
